import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faPrint, faSearch } from '@fortawesome/free-solid-svg-icons'
import { Globalize, PrintDocument, escapeHtml } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellEditEndingEventArgs, CellType, DataMap, FlexGrid, GridPanel, HeadersVisibility } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { of } from 'rxjs'

import {
    BaseComponent,
    FeatureType,
    Grid,
    HrApplicantStatus,
    HrGoal,
    HrHiringNeed,
    HrOffer,
    HrSurvey,
    ListItem,
    ModalSize,
    VpUser,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { HrApplicantStatusComponent } from './modals/hr-applicant-status.component'
import { HrGoalComponent } from './modals/hr-goal.component'
import { HrHiringNeedComponent } from './modals/hr-hiring-need.component'
import { HrOfferComponent } from './modals/hr-offer.component'
import { HrSurveyComponent } from './modals/hr-survey.component'

@Component({
    templateUrl: './scorecard.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        GridExportComponent,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
    ],
})
export class ScorecardComponent extends BaseComponent {
    faSearch = faSearch
    faPrint = faPrint
    selectedDate = new Date()
    showAll = false
    active = 1
    allUsers: VpUser[] = []
    allUserMap!: DataMap
    goalDirectionMap!: DataMap
    weekCols = [] as string[]

    goalsGrid: Grid<any>
    hireNeedsGrid: Grid<any>
    applicantStatusGrid: Grid<any>
    surveysGrid: Grid<any>
    offersGrid: Grid<any>
    newHiresGrid: Grid<any>
    hires90Grid: Grid<any>
    hires180Grid: Grid<any>
    separatedGrid: Grid<any>
    disciplineGrid: Grid<any>
    disciplineGridFollowUp: Grid<any>
    benefitEligibleGrid: Grid<any>
    benefitCountsGrid: Grid<any>

    exportGrids: FlexGrid[] = []
    exportGridNames: string[] = []

    get canViewGoals() {
        return this.services.authentication.hasFeature([FeatureType.HrScorecardGoalsView, FeatureType.HrScorecardAdminView])
    }
    get canEditGoals() {
        return this.services.authentication.hasFeature([FeatureType.HrScorecardGoalsEdit, FeatureType.HrScorecardAdminEdit])
    }
    get canViewHiring() {
        return this.services.authentication.hasFeature([FeatureType.HrScorecardHiringView, FeatureType.HrScorecardHiringEdit])
    }
    get canEditHiring() {
        return this.services.authentication.hasFeature([FeatureType.HrScorecardHiringEdit])
    }
    get canViewAdmin() {
        return this.services.authentication.hasFeature([FeatureType.HrScorecardAdminView])
    }
    get canEditAdmin() {
        return this.services.authentication.hasFeature([FeatureType.HrScorecardAdminEdit])
    }

    constructor() {
        super()

        this.goalsGrid = new Grid<any>(this.services)
            .wrapColumnHeaders()
            .setupModalEditing(this.services.data.hrGoals, 'Goal', HrGoalComponent, () => new HrGoal(), this, ModalSize.medium)
            .isReadOnly(false)
            .editOnDoubleClick(false)
        //     .allowDragging(AllowDragging.Both)
        // const reordered = () => {
        //     this.goalsGridReordered()
        // }
        // this.goalsGrid.dragged.on(reordered)

        this.hireNeedsGrid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.hrHiringNeeds,
                'Hiring Need',
                HrHiringNeedComponent,
                () => new HrHiringNeed(),
                this,
                ModalSize.large,
            )
            .wrapColumnHeaders()
        this.applicantStatusGrid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.hrApplicantStatuses,
                'Applicant Status',
                HrApplicantStatusComponent,
                () => new HrApplicantStatus(),
                this,
                ModalSize.medium,
            )
            .wrapColumnHeaders()
        this.surveysGrid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.hrSurveys,
                'Survey',
                HrSurveyComponent,
                () => {
                    const survey = new HrSurvey()
                    const orders = this.services.data.hrSurveys.items.map(item => item.displayOrder || 0)
                    survey.displayOrder = orders.length ? Math.max(...orders) + 1 : 1
                    return survey
                },
                this,
                ModalSize.medium,
            )
            .wrapColumnHeaders()
        this.offersGrid = new Grid<any>(this.services)
            .setupModalEditing(this.services.data.hrOffers, 'Offer', HrOfferComponent, () => new HrOffer(), this, ModalSize.medium)
            .wrapColumnHeaders()
        this.newHiresGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.hires90Grid = new Grid<any>(this.services).wrapColumnHeaders()
        this.hires180Grid = new Grid<any>(this.services).wrapColumnHeaders()
        this.separatedGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.disciplineGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.disciplineGridFollowUp = new Grid<any>(this.services).wrapColumnHeaders()
        this.benefitEligibleGrid = new Grid<any>(this.services).wrapColumnHeaders().isReadOnly(!this.canEditAdmin)
        this.benefitCountsGrid = new Grid<any>(this.services).wrapColumnHeaders()

        if (!this.canViewGoals) {
            this.active = this.canViewHiring ? 2 : 3
        }

        this.refresh()
        this.goalDirectionMap = new DataMap(
            [new ListItem(0, '>='), new ListItem(1, '<='), new ListItem(2, '>= <='), new ListItem(3, '=')],
            'value',
            'display',
        )
    }

    initGridsForExport(): void {
        this.exportGrids = []
        this.exportGridNames = []

        if (this.canViewGoals) {
            this.exportGrids.push(this.goalsGrid.flexGrid)
            this.exportGridNames.push('Goals')
        }

        if (this.canViewHiring) {
            this.exportGrids.push(
                ...[
                    this.hireNeedsGrid.flexGrid,
                    this.applicantStatusGrid.flexGrid,
                    this.surveysGrid.flexGrid,
                    this.offersGrid.flexGrid,
                    this.newHiresGrid.flexGrid,
                    this.hires90Grid.flexGrid,
                    this.hires180Grid.flexGrid,
                ],
            )
            this.exportGridNames.push(
                ...[
                    'Hiring Needs',
                    'Applicant Status',
                    'Surveys',
                    'Offers Made',
                    'New Hires - Past 7 Days',
                    'Hires Reaching 90 Days',
                    'Hires Reaching 180 Days',
                ],
            )
        }

        if (this.canViewAdmin) {
            this.exportGrids.push(
                ...[
                    this.separatedGrid.flexGrid,
                    this.disciplineGrid.flexGrid,
                    this.disciplineGridFollowUp.flexGrid,
                    this.benefitEligibleGrid.flexGrid,
                    this.benefitCountsGrid.flexGrid,
                ],
            )
            this.exportGridNames.push(
                ...[
                    'Separated Employees',
                    'Disciplinary Actions',
                    'Disciplinary Follow Ups This Month',
                    'Eligible for Benefits this Month',
                    'Benefits and Counts at Month Start',
                ],
            )
        }
    }
    refresh() {
        const params = new HttpParams().set('meetingId', 'E2BE00BA-5B8C-4815-A2A2-94909CA8330B')

        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/HrScorecard/GetScorecardData'),
                    this.canViewHiring ? this.services.data.hrHiringNeeds.queryAll() : of([]),
                    this.canViewHiring ? this.services.data.hrOffers.queryAll() : of([]),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                    this.canViewGoals
                        ? this.services.data.hrGoals.queryAllByAction('GetByMeetingId', params)
                        : of([]),
                    this.canViewHiring ? this.services.data.hrApplicantStatuses.queryAll() : of([]),
                    this.canViewHiring ? this.services.data.hrSurveys.queryAll() : of([]),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        // this.goalsGrid.items = result[0].goals
                        this.hireNeedsGrid.items = this.services.data.hrHiringNeeds.items
                        this.applicantStatusGrid.items = this.services.data.hrApplicantStatuses.items
                        this.surveysGrid.items = this.services.data.hrSurveys.items
                        this.offersGrid.items = this.services.data.hrOffers.items
                        this.newHiresGrid.items = result[0].newHires
                        this.hires90Grid.items = result[0].hires90
                        this.hires180Grid.items = result[0].hires180
                        this.separatedGrid.items = result[0].separations
                        this.disciplineGrid.items = result[0].disciplines
                        this.disciplineGridFollowUp.items = result[0].followUpDisciplines
                        this.benefitEligibleGrid.items = result[0].benefitEligibles
                        this.benefitCountsGrid.items = result[0].benefitCounts

                        this.allUsers = result[3] as VpUser[]
                        this.allUserMap = new DataMap(this.allUsers, 'id', 'name')
                        this.filterItems()
                        this.generateColumns()
                        this.initGridsForExport()
                    },
                }),
        )
    }

    filterItems() {
        this.goalsGrid.items = this.showAll ? this.services.data.hrGoals.items : this.services.data.hrGoals.items.filter(x => !x.isArchived)
    }

    // goalsGridReordered() {
    //     const goals = this.goalsGrid.view.items
    //     this.cancelableRequest(
    //         'reorder',
    //         true,
    //         this.services.data.load(true, true, this.services.data.put('/api/HrGoal/BulkUpdate', goals)).subscribe(),
    //     )
    // }

    generateColumns() {
        this.weekCols = []
        if (!this.goalsGrid.items.length) return
        setTimeout(() => {
            const cols = [] as string[]

            let indexOfMostWeekValues = 0
            let weekValuesLength = 0
            for (let i = 0; i < this.goalsGrid.items.length; i++) {
                const currentItemLength = this.goalsGrid.items[0].weekValues.length
                if (currentItemLength > weekValuesLength) {
                    indexOfMostWeekValues = i
                    weekValuesLength = currentItemLength
                }
            }

            const weeks = this.goalsGrid.items[indexOfMostWeekValues].weekValues
            for (const prop in weeks) {
                cols.push(prop)
            }
            this.weekCols = cols
        }, 1)
    }

    hires90180GridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.rows[r]
            const item = row.dataItem
            cell.classList.remove('red-cell')
            cell.classList.remove('green-cell')

            if (item.date > new Date()) {
                cell.classList.add('green-cell')
            } else {
                cell.classList.add('red-cell')
            }
        }
    }

    goalsGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.rows[r]
            const item = row.dataItem as HrGoal
            cell.classList.remove('red-cell')
            cell.classList.remove('green-cell')

            if (panel.grid.activeEditor) console.log(panel.grid.activeEditor)

            if (col.binding === 'goal1') {
                if (item.goalDirection < 2) {
                    cell.innerHTML = Globalize.format(item.goal1, item.format)
                } else {
                    cell.innerHTML = Globalize.format(item.goal1, item.format) + ' - ' + Globalize.format(item.goal2, item.format)
                }
            }
            if (col.binding === 'userId') {
                const originalVal = cell.innerHTML
                cell.title = originalVal
                cell.innerHTML = this.getInitials(originalVal)
            }

            if (col.binding === 'average') {
                cell.innerHTML = Globalize.format(item.average, item.format)
                if (!item.showAverage) cell.innerHTML = ''
            }

            if (col.binding === 'sum') {
                cell.innerHTML = Globalize.format(item.sum, item.format)

                // Don't show the sum for percents, since it doesn't really make sense.
                if (item.format[0].toUpperCase() === 'P' || !item.showSum) cell.innerHTML = ''
            }

            if (c > 5) {
                if (!panel.grid.activeEditor) {
                    // apply the format
                    const originalVal = item.weekValues[col.binding!.split('.')[1]]
                    cell.innerHTML = Globalize.format(originalVal, item.format)

                    // apply the goal meeting color
                    if (originalVal != null) {
                        switch (item.goalDirection) {
                            case 0:
                                if (originalVal >= item.goal1) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            case 1:
                                if (originalVal <= item.goal1) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            case 2:
                                if (originalVal >= item.goal1 && originalVal <= item.goal2) cell.classList.add('green-cell')
                                else cell.classList.add('red-cell')
                                break
                            default:
                        }
                    }
                }
            }
        }
    }

    // Event Handlers for the Goals grid
    beginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (!this.canEditGoals) args.cancel = true
        if (this.canEditAdmin) args.cancel = false

        // Don't allow the first date column to be editing if it 'IsCalculated'
        if (flexGrid.rows[args.row].dataItem.isCalculated && args.col === 6) {
            args.cancel = true
        }

        // Don't allow other date columns to be edited if the row isn't marked 'CanEditHistorical'
        if (!flexGrid.rows[args.row].dataItem.canEditHistorical && args.col > 6) {
            args.cancel = true
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        const item = flexGrid.rows[args.row].dataItem
        const key = flexGrid.columns[args.col].binding?.split('.')[1] as string
        this.bulkUpdateGoalValues([
            {
                id: item.id,
                date: this.getDateFromBinding(flexGrid.columns[args.col].binding),
                value: item.weekValues[key],
            },
        ])
    }

    pasting(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        args.cancel = true
        this.services.notification.info('Pasting is not yet supported.')
        // TODO: support pasting and pasted. An example is in the materials.component.ts file.
    }

    bulkUpdateGoalValues(values: any[]) {
        this.services.data
            .put('api/HrGoal/SaveGoalWeeks', values)
            .subscribe({ error: () => this.services.notification.error('The invoicing changes were not successfully saved') })
    }

    getDateFromBinding(binding: string | null) {
        // The binding can be split and parsed to get the date,
        // but last date has the year, so we start with that.
        const parts = binding?.split(' ') as string[]
        const month = parts[2]
        const day = +parts[3]
        const year = 2000 + +parts[4].replace("'", '')
        const endDate = new Date(year, this.monthMap[month], day)
        const startDate = endDate.addDays(-6)
        return startDate
    }

    benefitEligibleGridFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            cell.classList.remove('yellow-cell')
            if (col.binding === 'notes') {
                cell.classList.add('yellow-cell')
            }
        }
    }

    benefitEligibleGridCellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column mapping back to all users, so inactive user names still appear.
        const col = flexGrid.columns[args.col]
        const row = flexGrid.rows[args.row]
        const item = row.dataItem
        if (col.binding == 'notes') {
            this.services.data.put('api/HrScorecard/UpdateHrBenefitNote', item).subscribe({
                error: () => {
                    this.services.notification.error('The note was not successfully saved')
                },
            })
        }
    }

    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 100)
    }

    printDoc(grid) {
        // create PrintDocument
        const doc = new PrintDocument({
            title: 'PrintDocument Test',
            copyCss: false, // prevent cross-origin issues in jsfiddle
        })
        // add CSS explicitly (since we can't use copyCss in jsfiddle)
        doc.append('<link href="https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css" rel="stylesheet">')
        doc.append('<link href="https://cdn.mescius.com/wijmo/5.latest/styles/wijmo.min.css" rel="stylesheet">')
        // add some simple text
        doc.append('<h1>Printing Example</h1>')
        doc.append('<p>This document was created using the <b>PrintDocument</b> class.</p>')
        // add a printer-friendly version of a FlexGrid to the document
        doc.append("<p>Here's a FlexGrid rendered as a table:</p>")
        const tbl = this._renderTable(grid)
        doc.append(tbl)
        // print the document
        doc.print()
    }

    // renders grid as a table
    private _renderTable(flex: FlexGrid) {
        // start table
        let tbl = '<table>'
        // headers
        if (flex.headersVisibility & HeadersVisibility.Column) {
            tbl += '<thead>'
            for (let r = 0; r < flex.columnHeaders.rows.length; r++) {
                tbl += this._renderRow(flex.columnHeaders, r)
            }
            tbl += '</thead>'
        }
        // body
        tbl += '<tbody>'
        for (let r = 0; r < flex.rows.length; r++) {
            tbl += this._renderRow(flex.cells, r)
        }
        tbl += '</tbody>'
        // done
        tbl += '</table>'
        return tbl
    }

    private _renderRow(panel: GridPanel, r: number) {
        let tr = ''
        const row = panel.rows[r]
        if (row.renderSize > 0) {
            tr += '<tr>'
            for (let c = 0; c < panel.columns.length; c++) {
                const col = panel.columns[c]
                if (col.renderSize > 0) {
                    // get cell style, content
                    const style = 'width:' + col.renderSize + 'px;' + 'text-align:' + col.getAlignment() + ';' + 'padding-right: 6px'
                    let content = panel.getCellData(r, c, true)
                    if (!row.isContentHtml && !col.isContentHtml) {
                        content = escapeHtml(content)
                    }
                    // add cell to row
                    if (panel.cellType == CellType.ColumnHeader) {
                        tr += '<th style="' + style + '">' + content + '</th>'
                    } else {
                        // show boolean values as checkboxes
                        const raw = panel.getCellData(r, c, false)
                        if (raw === true) {
                            content = '&#9745;'
                        } else if (raw === false) {
                            content = '&#9744;'
                        }
                        tr += '<td style="' + style + '">' + content + '</td>'
                    }
                }
            }
            tr += '</tr>'
        }
        return tr
    }

    private getInitials(fullName: string): string {
        // Split the full name by spaces
        const nameParts = fullName.trim().split(' ')

        // Get the first initial from each part and convert them to uppercase
        const firstInitial = nameParts[0].charAt(0).toUpperCase()
        const secondInitial = nameParts[1].charAt(0).toUpperCase()

        // Return the initials
        return firstInitial + secondInitial
    }

    private monthMap: Record<string, number> = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
    }
}
